import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../contexts/AuthContext';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { checkBillingCards } from '../functions/billingUtils';
import { FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';

export default function BillingCheckModal({ practitionerId, open, onClose, onCardValidated }) {
  const [modalMessage, setModalMessage] = useState('');
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [availableCards, setAvailableCards] = useState([]);
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!open || !practitionerId) {
      return;
    }

    const checkCards = async () => {
      try {
        const cards = await checkBillingCards(practitionerId);
        setAvailableCards(cards);

        if (cards.length === 1) {
          onCardValidated({
            cardId: cards[0].cardId,
            brand: cards[0].brand,
            last4: cards[0].last4,
            type: cards[0].type
          });
        } else if (cards.length === 0) {
          const message = currentUser.role === 'practitioner'
            ? 'A valid card on file is required to proceed. Please add a payment method.'
            : 'A valid card on file is required to proceed. Please have the practitioner add a valid card.';
          setModalMessage(message);
        } else {
          setModalMessage('Please select a card to use for this payment.');
          setSelectedCardId(cards[0].cardId); // Default to first card
        }
      } catch (err) {
        console.error('BillingCheckModal: Error in checkBillingCards', err);
        let message = 'An error occurred while checking your payment method.';
        if (err.message === 'NO_ENABLED_CARD' || err.message === 'NO_VALID_CARD') {
          message = currentUser.role === 'practitioner'
            ? 'A valid card on file is required to proceed. Please add a payment method.'
            : 'A valid card on file is required to proceed. Please have the practitioner add a valid card.';
        } else if (err.message === 'CARD_EXPIRED') {
          message = 'Your card(s) on file have expired. Please update your payment method to proceed.';
        } else if (err.message === 'NO_BILLING_DOCUMENT') {
          message = 'No billing information found. Please add a payment method to proceed.';
        }
        setModalMessage(message);
        setAvailableCards([]);
      }
    };

    checkCards();
  }, [open, practitionerId, onCardValidated, currentUser.role]);

  const handleNavigateToBilling = () => {
    navigate(`/practitioners/${practitionerId}/billing`);
    onClose();
  };

  const handleCardSelection = () => {
    if (!selectedCardId) {
      setModalMessage('Please select a card.');
      return;
    }
    const selectedCard = availableCards.find(card => card.cardId === selectedCardId);
    onCardValidated({
      cardId: selectedCard.cardId,
      brand: selectedCard.brand,
      last4: selectedCard.last4,
      type: selectedCard.type
    });
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        onClose();
      }}
      aria-labelledby="billing-error-modal"
      aria-describedby="billing-error-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 1,
          outline: 'none',
        }}
      >
        <Typography id="billing-error-modal" variant="h6" component="h2" gutterBottom>
          Payment Method Required
        </Typography>
        <Typography id="billing-error-description" sx={{ mb: 3 }}>
          {modalMessage || 'Please select a payment method to proceed.'}
        </Typography>
        {availableCards.length > 1 && (
          <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
            <RadioGroup
              value={selectedCardId}
              onChange={(e) => setSelectedCardId(e.target.value)}
            >
              {availableCards.map((card) => (
                <FormControlLabel
                  key={card.cardId}
                  value={card.cardId}
                  control={<Radio />}
                  label={`${card.type.charAt(0).toUpperCase() + card.type.slice(1)} Card: ${card.brand} ending in ${card.last4}`}
                />
              ))}
            </RadioGroup>
          </FormControl>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              onClose();
            }}
          >
            Cancel
          </Button>
          {availableCards.length > 1 ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleCardSelection}
              disabled={!selectedCardId}
            >
              Select Card
            </Button>
          ) : currentUser.role === 'practitioner' ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleNavigateToBilling}
            >
              Go to Billing
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                onClose();
              }}
            >
              Close
            </Button>
          )}
        </Box>
      </Box>
    </Modal>
  );
}